import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Flag, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import { BulaLogo } from "@/components/BulaLogo";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    const r = await resetPassword(token, pw);
    setLoading(false);
    if (r.ok) {
      toast.success("Password reset — you're signed in!");
      navigate("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F3] p-6">
        <div className="card-default p-8 max-w-md text-center" data-testid="reset-invalid-card">
          <h1 className="font-heading font-black text-2xl">Invalid reset link</h1>
          <p className="text-[#0F2537]/70 text-sm mt-2">This link is missing a token. Request a fresh one to continue.</p>
          <Link to="/forgot-password" data-testid="reset-request-new-link"
            className="inline-block mt-6 px-5 py-3 rounded-xl bg-[#0F2537] text-white font-bold hover:bg-[#0F2537]/90 transition">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8F7F3]">
      <div className="relative hidden md:block">
        <img src="https://images.unsplash.com/photo-1538648759472-7251f7cb2c2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2537]/90 to-[#0F2537]/40" />
        <div className="relative z-10 h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="reset-logo">
            <BulaLogo size={36} />
            <span className="font-heading font-black text-xl">Bula Golf</span>
          </Link>
          <div>
            <div className="label-eyebrow text-white/60 mb-2">Reset password</div>
            <h2 className="font-heading font-black text-4xl leading-tight">One last swing.</h2>
            <p className="text-white/70 mt-3 max-w-sm text-sm leading-relaxed">Pick a fresh password and we'll log you straight in.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md card-default p-8 rise" data-testid="reset-form">
          <span className="label-eyebrow">Set a new password</span>
          <h1 className="font-heading font-black text-3xl mt-1">New password</h1>
          <p className="text-[#0F2537]/70 text-sm mt-1">Choose something memorable — at least 6 characters.</p>

          <label className="block mt-6 text-sm font-semibold">New password</label>
          <div className="mt-1 relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F2537]/40" />
            <input type={show ? "text" : "password"} required value={pw} onChange={(e) => setPw(e.target.value)}
              data-testid="reset-new-password-input" minLength={6}
              className="w-full pl-10 pr-10 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
            <button type="button" onClick={() => setShow((v) => !v)} data-testid="reset-toggle-show-btn"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0F2537]/40 hover:text-[#0F2537]">
              {show ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <label className="block mt-4 text-sm font-semibold">Confirm password</label>
          <input type={show ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm-password-input" minLength={6}
            className="mt-1 w-full px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />

          <button type="submit" disabled={loading} data-testid="reset-submit-btn"
            className="mt-6 w-full py-3 rounded-xl bg-[#E05D3A] text-white font-bold hover:bg-[#E05D3A]/90 transition disabled:opacity-60">
            {loading ? "Resetting…" : "Reset password & sign in"}
          </button>

          <p className="mt-5 text-sm text-[#0F2537]/70 text-center">
            <Link to="/login" data-testid="reset-back-to-login" className="font-semibold">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
